import moment from 'moment';
import { FilterOperator, IRange, IUser } from '../../../extViewmodels';
import { ISelectOption } from '../../../web/components/DeprecatedSelect';
import { filterByChecked, reduceChildren } from '../../components/leadView/components/OutcomeSelect/utils';
import { FieldKey } from '../../viewModels/form';

export function formatDate<T>(property: T, value?: IRange<Date>) {
	if (!value) {
		return null;
	}

	const f = 'YYYY-MM-DD';
	const criteria = {
		criteria: [
			{
				op: FilterOperator.Gt,
				property,
				value: moment(value?.start).format(f),
			},
			{
				op: FilterOperator.Lt,
				property,
				value: moment(value?.end).add(1, 'day').format(f),
			},
		],
		op: FilterOperator.And,
	};

	return criteria;
}

export function formatUser<T>(property: T, value?: IUser) {
	if (!value?.id) {
		return null;
	}

	return {
		property,
		value: value?.id,
	};
}

export function formatUsers<T>(property: T, value?: IUser[]) {
	if (!value || value.length === 0) {
		return null;
	}

	// If only one user is selected, return a simple criteria
	if (value.length === 1) {
		return {
			property,
			value: value[0].id,
		};
	}

	// For multiple users, create OR criteria array
	const userCriteria = value.map(user => ({
		property,
		value: user.id,
	}));

	return {
		criteria: [...userCriteria],
		op: FilterOperator.Or,
	};
}

export function formatOutcomes<T>(property: T, value?: ISelectOption<FieldKey>[]) {
	if (!value || value.length < 1) {
		return null;
	}

	const criteriaObject = (item: { name: string }) => ({
		property,
		value: item.name,
	});
	const filteredOutcome = value?.reduce(reduceChildren, [])?.filter(filterByChecked)?.map(criteriaObject);
	if (!filteredOutcome?.length) {
		return null;
	}
	return {
		criteria: filteredOutcome,
		op: FilterOperator.Or,
	};
}

export function formatOutcomeNames<T>(property: T, names?: string[]) {
	if (!names?.length) {
		return null;
	}
	return {
		criteria: names.map(value => ({ property, value })),
		op: FilterOperator.Or,
	};
}

export function formatOptional<T>(property: T, value?: string) {
	if (!value) {
		return null;
	}

	return {
		property,
		value,
	};
}

export function formatSelect<T>(property: T, value?: ISelectOption<any>) {
	if (!value?.dataContext) {
		return null;
	}

	return {
		property,
		value: value.dataContext,
	};
}

export function formatTeams<T>(property: T, value?: string[]) {
	if (!value || value.length === 0) {
		return null;
	}

	// If only one team is selected, return a simple criteria
	if (value.length === 1) {
		return {
			property,
			value: value[0],
		};
	}

	// For multiple teams, create OR criteria array as specified by user
	const teamCriteria = value.map(team => ({
		property,
		value: team,
	}));

	return {
		criteria: [...teamCriteria],
		op: FilterOperator.Or,
	};
}

export function formatDealStages<T>(property: T, value?: string[]) {
	if (!value || value.length === 0) {
		return null;
	}

	return {
		property,
		value: value.join(','),
	};
}

export function formatDurationRange<T>(property: T, value?: IRange<number>) {
	if (!value) {
		return null;
	}

	const criteria: { op: FilterOperator; property: T; value: string }[] = [];

	if (value.start != null && value.start > 0) {
		criteria.push({ op: FilterOperator.Gt, property, value: String(value.start) });
	}

	if (value.end != null && value.end > 0) {
		criteria.push({ op: FilterOperator.Lt, property, value: String(value.end) });
	}

	if (criteria.length === 0) {
		return null;
	}

	if (criteria.length === 1) {
		return criteria[0];
	}

	return {
		criteria,
		op: FilterOperator.And,
	};
}
